import * as React from "react";
import {Link as RouterLink, useNavigate, useParams} from "react-router-dom";
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    Link,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import {toast} from "react-toastify";
import LoadingDialog from "../../components/loadingDialog";
import SingleSelect from "../../components/SingleSelect";
import {
    CLASS_OPTIONS,
    DEGREE_OPTIONS,
    EDU_LEVELS,
    WORKING_OPTIONS,
    fetchHpEvent,
    getFieldTemplateById,
    getProgramSetOptionById,
    hpEventUpsert,
    hsYearOptions,
    parseCsvInts,
    searchBrokersOptions,
    searchFieldTemplateOptions,
    searchProgramSetOptions,
    searchSchoolsOptions,
} from "./eventsService";

const labelSx = {
    fontSize: 12.5,
    lineHeight: 1.2,
    py: 0.5,
    pr: 1,
    color: "text.secondary",
    display: "flex",
    alignItems: "center",
    minWidth: 180,
};
const fieldSx = {
    "& .MuiInputBase-root": {fontSize: 12.5, minHeight: 30},
    "& .MuiInputBase-input": {py: 0.5, px: 1},
    "& .MuiSelect-select": {py: 0.5, px: 1},
};
const checkLabelSx = {mr: 1, ml: 0, my: 0, "& .MuiFormControlLabel-label": {fontSize: 12.5}};
const denseSelectSx = {minWidth: 120, ...fieldSx};
const singleSelectDenseSx = {
    flex: 1,
    minWidth: 220,
    "& .MuiButton-root": {
        minHeight: 30,
        height: 30,
        py: 0,
        px: 1,
        fontSize: 12.5,
        lineHeight: 1.2,
        "& .MuiSvgIcon-root": {fontSize: 18},
    },
};

function Row(props: {label: React.ReactNode; children: React.ReactNode}) {
    return (
        <Box sx={{display: "flex", alignItems: "flex-start", gap: 1, mb: 1}}>
            <Box sx={labelSx}>{props.label}</Box>
            <Box sx={{flex: 1, minWidth: 0}}>{props.children}</Box>
        </Box>
    );
}

export default function EventUpsertPage() {
    const {id: idParam} = useParams();
    const navigate = useNavigate();
    const isNew = !idParam || idParam === "new";
    const eventId = isNew ? 0 : Number(idParam);

    const [loading, setLoading] = React.useState(!isNew);
    const [saving, setSaving] = React.useState(false);

    const [school, setSchool] = React.useState<any>(null);
    const [broker, setBroker] = React.useState<any>(null);
    const [programSet, setProgramSet] = React.useState<any>(null);
    const [programSetIdTyped, setProgramSetIdTyped] = React.useState("");
    const [fieldTemplate, setFieldTemplate] = React.useState<any>(null);
    const [postUrl, setPostUrl] = React.useState("");
    const [active, setActive] = React.useState(true);
    const [createNewCopy, setCreateNewCopy] = React.useState(false);

    const [constraintMilitary, setConstraintMilitary] = React.useState<string>("");
    const [constraintGender, setConstraintGender] = React.useState<string>("");
    const [hsLower, setHsLower] = React.useState<string>("");
    const [hsHigher, setHsHigher] = React.useState<string>("");
    const [eduLower, setEduLower] = React.useState<string>("");
    const [eduHigher, setEduHigher] = React.useState<string>("");
    const [degrees, setDegrees] = React.useState<Record<number, boolean>>({});
    const [classes, setClasses] = React.useState<Record<number, boolean>>({});
    const [working, setWorking] = React.useState<Record<number, boolean>>({});

    const years = React.useMemo(() => hsYearOptions(), []);

    React.useEffect(() => {
        document.title = isNew ? "New Post Event | DN" : `Update Post Event ${eventId} | DN`;
    }, [isNew, eventId]);

    React.useEffect(() => {
        if (isNew) {
            setLoading(false);
            return;
        }
        setLoading(true);
        fetchHpEvent(eventId).then(async (d) => {
            if (!d) {
                toast.error("No post event found.");
                setLoading(false);
                navigate("/events/new");
                return;
            }
            setPostUrl(d.postUrl || "");
            setActive(!!d.active);
            setSchool({
                id: d.schoolId,
                value: `${d.schoolName} (${d.schoolId})`,
                schoolId: d.schoolId,
                schoolName: d.schoolName,
            });
            if (d.brokerId) {
                setBroker({
                    id: d.brokerId,
                    value: `${d.brokerName} (${d.brokerId})`,
                    brokerId: d.brokerId,
                    brokerName: d.brokerName,
                });
            } else {
                setBroker(null);
            }
            setProgramSet({
                id: d.programSetId,
                value: `${d.programSetName} [${d.programSetId}]`,
                programSetId: d.programSetId,
                programSetName: d.programSetName,
            });
            setProgramSetIdTyped(String(d.programSetId));
            if (d.hpPostEventId) {
                const tpl = await getFieldTemplateById(d.hpPostEventId, !d.active);
                setFieldTemplate(tpl);
            } else {
                setFieldTemplate(null);
            }
            setConstraintMilitary(d.constraintMilitary == null ? "" : String(d.constraintMilitary));
            setConstraintGender(d.constraintGender == null ? "" : String(d.constraintGender));
            setHsLower(d.constraintHsLower == null ? "" : String(d.constraintHsLower));
            setHsHigher(d.constraintHsHigher == null ? "" : String(d.constraintHsHigher));
            setEduLower(d.constraintEduLower == null ? "" : String(d.constraintEduLower));
            setEduHigher(d.constraintEduHigher == null ? "" : String(d.constraintEduHigher));
            const deg: Record<number, boolean> = {};
            parseCsvInts(d.constraintDegreeList).forEach((n) => {
                deg[n] = true;
            });
            setDegrees(deg);
            const cls: Record<number, boolean> = {};
            parseCsvInts(d.constraintClassesList).forEach((n) => {
                cls[n] = true;
            });
            setClasses(cls);
            const wrk: Record<number, boolean> = {};
            parseCsvInts(d.constraintWorkingList).forEach((n) => {
                wrk[n] = true;
            });
            setWorking(wrk);
            setCreateNewCopy(false);
            setLoading(false);
        });
    }, [eventId, isNew, navigate]);

    const applyProgramSetId = async () => {
        const id = Number(programSetIdTyped);
        if (!Number.isFinite(id) || id <= 0) {
            toast.error("Enter a valid program set id");
            return;
        }
        const opt = await getProgramSetOptionById(id);
        setProgramSet(opt);
        setProgramSetIdTyped(String(opt.id));
    };

    const openFields = () => {
        const templateId = fieldTemplate?.id || fieldTemplate?.hpPostEventId;
        if (!templateId) {
            toast.error("Select a field template first (not New)");
            return;
        }
        const eid = eventId > 0 ? eventId : 0;
        navigate(`/events/${eid}/fields?hpPostEventId=${templateId}`);
    };

    const save = async () => {
        if (!school?.id) {
            toast.error("Select a school");
            return;
        }
        const programSetId = Number(programSet?.id || programSetIdTyped);
        if (!Number.isFinite(programSetId) || programSetId <= 0) {
            toast.error("Select a program set");
            return;
        }
        if (!String(postUrl).trim()) {
            toast.error("Client Post URL is required");
            return;
        }

        setSaving(true);
        const input = {
            id: eventId,
            hpPostEventId: fieldTemplate?.id || null,
            postUrl: String(postUrl).trim(),
            schoolId: Number(school.id),
            brokerId: broker?.id || null,
            programSetId,
            active,
            createNewCopy: !isNew && createNewCopy,
            constraintMilitary: constraintMilitary === "" ? null : Number(constraintMilitary),
            constraintGender: constraintGender === "" ? null : Number(constraintGender),
            constraintHsLower: hsLower === "" ? null : Number(hsLower),
            constraintHsHigher: hsHigher === "" ? null : Number(hsHigher),
            constraintEduLower: eduLower === "" ? null : Number(eduLower),
            constraintEduHigher: eduHigher === "" ? null : Number(eduHigher),
            constraintDegreeList: DEGREE_OPTIONS.filter((d) => degrees[d.id]).map((d) => d.id),
            constraintClassesList: CLASS_OPTIONS.filter((c) => classes[c.id]).map((c) => c.id),
            constraintWorkingList: WORKING_OPTIONS.filter((w) => working[w.id]).map((w) => w.id),
        };

        const saved = await hpEventUpsert(input);
        setSaving(false);
        if (!saved) {
            toast.error("Unable to save post event");
            return;
        }
        toast.success(isNew || createNewCopy ? "Post event created" : "Post event updated");
        if (isNew || createNewCopy || saved.id !== eventId) {
            navigate(`/events/${saved.id}`);
        } else {
            setFieldTemplate({
                id: saved.hpPostEventId,
                value: fieldTemplate?.value || `Field template [${saved.hpPostEventId}]`,
                hpPostEventId: saved.hpPostEventId,
            });
        }
    };

    if (loading) {
        return <LoadingDialog />;
    }

    return (
        <Box sx={{p: 2, maxWidth: 920}}>
            <Typography variant="h5" sx={{mb: 0.5}}>
                {isNew ? "New Post Event" : "Update Post Event"}
                {!isNew ? (
                    <Typography component="span" sx={{ml: 1, fontSize: 14}}>
                        (
                        <Link component={RouterLink} to="/events/new">
                            NEW
                        </Link>
                        )
                    </Typography>
                ) : null}
            </Typography>

            <Row label="Event ID">
                <Typography sx={{fontSize: 13, pt: 0.5}}>{isNew ? "—" : eventId}</Typography>
            </Row>

            <Row label="Field template">
                <Box sx={{display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center"}}>
                    <Box sx={singleSelectDenseSx}>
                        <SingleSelect
                            searchFunction={searchFieldTemplateOptions}
                            selectedItem={fieldTemplate}
                            setSelectedItem={setFieldTemplate}
                            placeholder="New field template"
                        />
                    </Box>
                    <Button size="small" onClick={() => setFieldTemplate(null)}>
                        New
                    </Button>
                    <Button size="small" variant="outlined" onClick={openFields}>
                        Update Event Fields
                    </Button>
                </Box>
            </Row>

            <Row label="Client Post URL">
                <TextField
                    fullWidth
                    size="small"
                    sx={fieldSx}
                    value={postUrl}
                    onChange={(e) => setPostUrl(e.target.value)}
                    placeholder="email if batch"
                />
            </Row>

            <Row label="School">
                <Box sx={singleSelectDenseSx}>
                    <SingleSelect
                        searchFunction={searchSchoolsOptions}
                        selectedItem={school}
                        setSelectedItem={setSchool}
                        placeholder="School"
                    />
                </Box>
            </Row>

            <Row label="Broker">
                <Box sx={singleSelectDenseSx}>
                    <SingleSelect
                        searchFunction={searchBrokersOptions}
                        selectedItem={broker}
                        setSelectedItem={setBroker}
                        placeholder="Broker"
                    />
                </Box>
            </Row>

            <Row label="Program Set">
                <Box sx={{display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center"}}>
                    <Box sx={singleSelectDenseSx}>
                        <SingleSelect
                            searchFunction={searchProgramSetOptions}
                            selectedItem={programSet}
                            setSelectedItem={(item: any) => {
                                setProgramSet(item);
                                setProgramSetIdTyped(item ? String(item.id) : "");
                            }}
                            placeholder="Program set"
                        />
                    </Box>
                    <Typography sx={{fontSize: 12.5}}>or id</Typography>
                    <TextField
                        size="small"
                        sx={{...fieldSx, width: 100}}
                        value={programSetIdTyped}
                        onChange={(e) => setProgramSetIdTyped(e.target.value)}
                    />
                    <Button size="small" onClick={applyProgramSetId}>
                        Apply
                    </Button>
                </Box>
            </Row>

            <Row label="Active">
                <FormControl size="small" sx={denseSelectSx}>
                    <Select
                        value={active ? "1" : "0"}
                        onChange={(e) => setActive(e.target.value === "1")}
                    >
                        <MenuItem value="1">Yes</MenuItem>
                        <MenuItem value="0">No</MenuItem>
                    </Select>
                </FormControl>
            </Row>

            <Row label="Constraint Military">
                <FormControl size="small" sx={denseSelectSx}>
                    <Select
                        displayEmpty
                        value={constraintMilitary}
                        onChange={(e) => setConstraintMilitary(String(e.target.value))}
                    >
                        <MenuItem value="">&nbsp;</MenuItem>
                        <MenuItem value="1">Military ONLY</MenuItem>
                        <MenuItem value="0">NO Military</MenuItem>
                    </Select>
                </FormControl>
            </Row>

            <Row label="Constraint Gender">
                <FormControl size="small" sx={denseSelectSx}>
                    <Select
                        displayEmpty
                        value={constraintGender}
                        onChange={(e) => setConstraintGender(String(e.target.value))}
                    >
                        <MenuItem value="">&nbsp;</MenuItem>
                        <MenuItem value="1">Male ONLY</MenuItem>
                        <MenuItem value="2">Female ONLY</MenuItem>
                        <MenuItem value="4">Pending ONLY</MenuItem>
                    </Select>
                </FormControl>
            </Row>

            <Row label="Constraint High School">
                <Box sx={{display: "flex", gap: 1, alignItems: "center"}}>
                    <FormControl size="small" sx={denseSelectSx}>
                        <Select
                            displayEmpty
                            value={hsLower}
                            onChange={(e) => setHsLower(String(e.target.value))}
                        >
                            <MenuItem value="">&nbsp;</MenuItem>
                            {years.map((y) => (
                                <MenuItem key={`lo-${y}`} value={String(y)}>
                                    {y}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography sx={{fontSize: 12.5}}>to</Typography>
                    <FormControl size="small" sx={denseSelectSx}>
                        <Select
                            displayEmpty
                            value={hsHigher}
                            onChange={(e) => setHsHigher(String(e.target.value))}
                        >
                            <MenuItem value="">&nbsp;</MenuItem>
                            {years.map((y) => (
                                <MenuItem key={`hi-${y}`} value={String(y)}>
                                    {y}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Row>

            <Row label="Constraint Education Level">
                <Box sx={{display: "flex", gap: 1, alignItems: "center"}}>
                    <FormControl size="small" sx={{...denseSelectSx, minWidth: 180}}>
                        <Select
                            displayEmpty
                            value={eduLower}
                            onChange={(e) => setEduLower(String(e.target.value))}
                        >
                            <MenuItem value="">&nbsp;</MenuItem>
                            {EDU_LEVELS.map((e) => (
                                <MenuItem key={`edu-lo-${e.id}`} value={String(e.id)}>
                                    {e.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography sx={{fontSize: 12.5}}>to</Typography>
                    <FormControl size="small" sx={{...denseSelectSx, minWidth: 180}}>
                        <Select
                            displayEmpty
                            value={eduHigher}
                            onChange={(e) => setEduHigher(String(e.target.value))}
                        >
                            <MenuItem value="">&nbsp;</MenuItem>
                            {EDU_LEVELS.map((e) => (
                                <MenuItem key={`edu-hi-${e.id}`} value={String(e.id)}>
                                    {e.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Row>

            <Row label="Constraint Degree Seeking">
                <Box sx={{display: "flex", flexWrap: "wrap"}}>
                    {DEGREE_OPTIONS.map((d) => (
                        <FormControlLabel
                            key={d.id}
                            sx={checkLabelSx}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={!!degrees[d.id]}
                                    onChange={(e) =>
                                        setDegrees((prev) => ({...prev, [d.id]: e.target.checked}))
                                    }
                                />
                            }
                            label={d.label}
                        />
                    ))}
                </Box>
            </Row>

            <Row label="Constraint Classes">
                <Box sx={{display: "flex", flexWrap: "wrap"}}>
                    {CLASS_OPTIONS.map((c) => (
                        <FormControlLabel
                            key={c.id}
                            sx={checkLabelSx}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={!!classes[c.id]}
                                    onChange={(e) =>
                                        setClasses((prev) => ({...prev, [c.id]: e.target.checked}))
                                    }
                                />
                            }
                            label={c.label}
                        />
                    ))}
                </Box>
            </Row>

            <Row label="Constraint Working">
                <Box sx={{display: "flex", flexWrap: "wrap"}}>
                    {WORKING_OPTIONS.map((w) => (
                        <FormControlLabel
                            key={w.id}
                            sx={checkLabelSx}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={!!working[w.id]}
                                    onChange={(e) =>
                                        setWorking((prev) => ({...prev, [w.id]: e.target.checked}))
                                    }
                                />
                            }
                            label={w.label}
                        />
                    ))}
                </Box>
            </Row>

            {!isNew ? (
                <Row label="Create NEW event">
                    <FormControlLabel
                        sx={checkLabelSx}
                        control={
                            <Checkbox
                                size="small"
                                checked={createNewCopy}
                                onChange={(e) => setCreateNewCopy(e.target.checked)}
                            />
                        }
                        label="Create NEW event, leaves existing unchanged"
                    />
                </Row>
            ) : null}

            <Box sx={{mt: 2}}>
                <Button variant="contained" onClick={save} disabled={saving}>
                    {saving ? "Saving…" : "Submit"}
                </Button>
            </Box>
        </Box>
    );
}
